import { inject, Injectable, signal } from '@angular/core';
import { ApiHandlerService } from './api-base.service';
import { SessionService } from './session.service';
import { map, Subject } from 'rxjs';
import { ReportTemplate, ReportSearchParams } from '../interfaces';
import { Observable } from 'rxjs';

@Injectable()
export class TemplateService {
  apiHandlerService = inject(ApiHandlerService);
  sessionService = inject(SessionService);
  template = signal<ReportTemplate | null | undefined>(undefined);

  private templateChanged = new Subject<ReportTemplate | null>();

  templatesList = signal<ReportTemplate[] | undefined>(undefined);

  saveTemplate(
    templateName: string | undefined | null,
    templateDescription: string | undefined | null,
    params: any,
    reportId: number | undefined,
    templateId?: string | undefined | null,
    isTemporary = true
  ): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'save_report_template',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId || null,
        p_template_id: templateId || null,
        p_is_temporary: isTemporary,
        p_template_desc: templateDescription || null,
        p_template_name: templateName || null,
        p_input_params: params,
      },
    };

    return this.apiHandlerService.Post(url, body).pipe(
      map((resp: any) => {
        if (resp?.error_message) {
          throw new Error(resp.error_message);
        }
        return resp as ReportTemplate;
      })
    );
  }

  getTemplateById(
    reportId: number,
    templateId: string
  ): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_report_templates',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId,
        p_template_id: templateId,
        p_is_temporary: false,
      },
    };

    return this.apiHandlerService
      .Post<any, ReportTemplate[] | any>(url, body)
      .pipe(
        map((response) => {
          const template = response[0];
          if (
            template &&
            (template.errorCode || template.successful === 'false')
          ) {
            throw new Error(template.message);
          }
          
          return template;
        })
      );
  }

  getAllTemplates(reportId: number | undefined): Observable<ReportTemplate[]> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_report_templates',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_report_id: reportId || null,
        p_template_id: null,
      },
    };

    return this.apiHandlerService.Post(url, body);
  }

  deleteTemplate(templateId: string): Observable<ReportTemplate> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'delete_report_template',
      'ce'
    );

    const body = {
      p_report_params: {
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [this.sessionService.sessionData?.Domains],
        p_template_id: templateId,
      },
    };

    return this.apiHandlerService.Post(url, body);
  }

  setSearchParams(params: ReportSearchParams): void {
    const url = new URL(window.location.href);
    const currentQueryParams = Object.keys(params).reduce(
      (acc: ReportSearchParams, key) => {
        acc[key] = url.searchParams.get(key);
        return acc;
      },
      {}
    );
    Object.keys(params).forEach((key) => params[key] ? url.searchParams.set(key, params[key]) : url.searchParams.delete(key));
    const updatedQueryParams = Object.keys(params).reduce(
      (acc: ReportSearchParams, key) => {
        acc[key] = url.searchParams.get(key);
        return acc;
      },
      {}
    );
    const isParamsChanged =  Object.keys(params).some(key => currentQueryParams[key] !== updatedQueryParams[key]);
    if(isParamsChanged) {
      window.history.pushState({}, '', url.toString());
    }
  }

   updateTemplateList(template: ReportTemplate) {
    this.templatesList.update((arr) => {
      const list = [...(arr as ReportTemplate[])];
      const index = list.findIndex(
        (t) => t.template_id === template.template_id
      );

      if (index !== -1) {
        list[index] = template;
      } else {
        list.push(template);
      }

      return list;
    });
  }

  baseTemplate(): ReportTemplate | null | undefined {
    const url = new URL(window.location.href);
    const templateName = url.searchParams.get('templateName');
    const modificationId = url.searchParams.get('modificationId');
    const templateId = url.searchParams.get('templateId');
    return templateName && modificationId && templateId ? (this.templatesList() as ReportTemplate[]).find((item) => item.template_id === templateId && !item.is_temporary) : null;
  }

  get templateChangedSubject() {
    return this.templateChanged;
  }
}
